/**
 * Per-environment logging defaults — the one declarative place to change how much
 * the BFF logs in each environment. This is the "config file" for logging; the
 * `WRANGO_BFF_LOG_*` / `NEXT_PUBLIC_WRANGO_BROWSER_LOG_*` env vars (resolved in
 * `config.ts` / `browser-logging.ts`) override any single field at runtime, but
 * this table is the baseline.
 *
 * Follows the `src/lib/feature-flag.ts` per-env-table pattern — the platform
 * repo's TS analog of the Python services' layered YAML config. Plain objects, so
 * Next inlines them and the same table resolves in Node, Edge, and the browser
 * (a runtime-loaded YAML file could not).
 *
 * `local` is a fifth tier the deployed `PlatformEnv` set (dev/qa/staging/prod)
 * doesn't include: a developer's machine sets a personal or unset platform env,
 * which resolves here to `local`.
 */

import { PLATFORM_ENVS, type PlatformEnv } from "@/lib/env";

export type LogEnv = PlatformEnv | "local";

/**
 * The four header/body toggles any request/response logger can carry. Scalars
 * (method/path/status/duration) are always logged; these gate the heavy fields.
 * A field toggled off is omitted from the line entirely. Reusable across surfaces
 * — inbound, upstream, and any future logger that emits headers/bodies.
 */
export interface FieldToggles {
  requestHeaders: boolean;
  requestBody: boolean;
  responseHeaders: boolean;
  responseBody: boolean;
}

/** Everything a header/body logger can emit. */
const allFields: FieldToggles = {
  requestHeaders: true,
  requestBody: true,
  responseHeaders: true,
  responseBody: true,
};
/** Scalars only — no headers/bodies. */
const noFields: FieldToggles = {
  requestHeaders: false,
  requestBody: false,
  responseHeaders: false,
  responseBody: false,
};

export interface LoggingDefaults {
  /** Master switch for the inbound structured logs (`request` + `trpc_procedure`). */
  serverEnabled: boolean;
  /** Redactor over server log lines (sensitive keys + Bearer/Basic/DSN). */
  serverRedact: boolean;
  /** Inbound request logger (`bff.inbound` → `request`). */
  inbound: FieldToggles;
  /** Upstream request logger (`bff.upstream` → `upstream_request`). */
  upstream: FieldToggles;
  /** Edge page-load logger (`edge.proxy` → `page_load`). Only `requestHeaders` is
   *  applicable — the edge runs before the response and can't read a navigation body —
   *  so it's the only field the type offers. */
  edge: { requestHeaders: boolean };
  /** Browser fetch logging (`web.fetch` → `browser_request`): `ids` = ids+method/url/status; `full` adds bodies. */
  browserMode: "off" | "ids" | "full";
  /** Which browser requests to log: same-origin `/api/*` only, or every fetch. */
  browserScope: "api" | "all";
  /** Browser page-view logging (`web.pageview` → `page_view`) — one line per SPA navigation. */
  browserPageView: boolean;
  /** Redactor over browser log lines. */
  browserRedact: boolean;
}

/**
 * Local dev: quiet by default so a developer on Next's native request logging
 * isn't force-fed our JSON; raw (unredacted) values when logging is opted in, for
 * easy debugging against dev data.
 */
const local: LoggingDefaults = {
  serverEnabled: false,
  serverRedact: false,
  inbound: allFields,
  upstream: allFields,
  edge: { requestHeaders: true },
  browserMode: "full",
  browserScope: "api",
  browserPageView: true,
  browserRedact: false,
};

/** dev / qa / staging: full structured logs, redacted, for debugging. */
const nonProd: LoggingDefaults = {
  serverEnabled: true,
  serverRedact: true,
  inbound: allFields,
  upstream: allFields,
  edge: { requestHeaders: true },
  browserMode: "full",
  browserScope: "api",
  browserPageView: true,
  browserRedact: true,
};

/** prod: scalars only (headers/bodies can carry PHI), ids-only browser, redacted. */
const prod: LoggingDefaults = {
  serverEnabled: true,
  serverRedact: true,
  inbound: noFields,
  upstream: noFields,
  edge: { requestHeaders: false },
  browserMode: "ids",
  browserScope: "api",
  browserPageView: true,
  browserRedact: true,
};

export const LOGGING_DEFAULTS: Record<LogEnv, LoggingDefaults> = {
  local,
  dev: nonProd,
  qa: nonProd,
  staging: nonProd,
  prod,
};

/**
 * Resolve the logging environment: a deployed value (dev/qa/staging/prod, with
 * `stage` → staging to match `env.ts`), or `local` for a personal or unset
 * platform env. `NEXT_PUBLIC_WRANGO_PLATFORM_ENV` is inlined by Next, so this
 * resolves identically server-side, at the edge, and in the browser.
 */
export function logEnv(): LogEnv {
  const raw = process.env.NEXT_PUBLIC_WRANGO_PLATFORM_ENV?.trim().toLowerCase();
  if (raw === "stage") return "staging";
  if (raw && (PLATFORM_ENVS as readonly string[]).includes(raw)) return raw as PlatformEnv;
  return "local";
}

/** The defaults for the current environment. */
export function loggingDefaults(): LoggingDefaults {
  return LOGGING_DEFAULTS[logEnv()];
}
